import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, isNil, omitBy } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { routeToBoxesView } from "Helpers/index";
import moment from "moment";
import useSession from "Hooks/session.hook";
import { Row, Col } from "antd";
import orderStatusesMap from "Data/order-statuses.json";
import DataTableFilter from "Components/shared/data-table-filter";
import { DataTable } from "@life-line-lab/components-react";
import { BasicModal, Button } from "@life-line-lab/components-react";
import AssignDriverBoxesModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/assign-driver-boxes-modal";
import AssignDriverBulkLooseModal from "../../parcel-tracking-modals/assign-driver-bulk-loose-modal/assign-driver-bulk-loose";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import useAccessControlList from "Hooks/access-control.hook";
import AssignServicePointModal from "Components/delivery-management/parcel-tracking-modals/assign-service-point-modal/assign-service-point-modal";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";
import { routeToOrderView } from "Helpers/route-order-view";
import { LastMileStatusEnum } from "Types/last_mile_status.types";

export interface LastMileBoxesProps {
  onFilter?: any;
  onAssignDriver?: any;
  parcelList?: any;
  sortingHubOptions?: any;
  vehicleOptions?: any;
  onStatusUpdate?: any;
  handleUpdateReturnOrder: any;
  driverList?: any;
}

const LastMileOrderList: React.FC<LastMileBoxesProps> = ({
  onFilter,
  handleUpdateReturnOrder,
  parcelList,
  sortingHubOptions,
  vehicleOptions,
  onAssignDriver,
  driverList,
}) => {
  const { roles } = useSession();
  const { t } = useTranslation();
  const { roleTypes } = useSession();
  const history = useHistory();
  const location = useLocation();
  const initialModal: any = {
    open: false,
  };

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });

  const showUpdateOption = permission.rights.includes("UPDATE");

  const [callModal, setCallModal] = useState<any>(<></>);
  const [modal, setModal] = useState<any>(initialModal);

  const handleOpenAssignDriverBoxesModal = (record) => {
    setCallModal(
      <AssignDriverBoxesModal
        onSubmit={(value) => {
          onAssignDriver(value, record.type);
          handleResetModalState();
        }}
        vehicleOptions={vehicleOptions}
        boxInfo={record}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const handleOpenAssignDriverOthersModal = (record) => {
    setCallModal(
      <AssignDriverBulkLooseModal
        selectedOrder={record._id}
        selectedOrderDetails={record}
        vehicleOptions={vehicleOptions}
        onSubmit={(value) => {
          onAssignDriver(value, record.type);
          handleResetModalState();
        }}
        type={record?.driver ? "REASSIGN" : "ASSIGN"}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const handleOpenDiolkoReviewModal = (record) => {
    setCallModal(
      <AssignServicePointModal
        info={record}
        sortingHubOptions={sortingHubOptions}
        onSubmit={(value) => {
          handleUpdateReturnOrder(value);
          handleResetModalState();
        }}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const initialFilter: any = {
    type: "",
    status: "",
    originHub: "",
    driverID: "",
    unassignedDriver: "",
  };

  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);

  const handleOpenBoxesTrackingView = (boxID) => {
    routeToBoxesView(boxID, location, history);
  };

  const handleOpenOrderView = (orderID) => {
    const params = "module=delivery&tabKey=4";
    routeToOrderView(orderID, location, history, params);
  };

  const initialFilterSelection = [
    {
      key: "status",
      name: "status",
      label: t("placeholder.byStatus"),
      placeholder: t("placeholder.byStatus"),
      value: filterState?.status,
      options: [
        { label: t("option.all"), value: "" },
        {
          label: t("label.deliveryNotPossible"),
          value: LastMileStatusEnum.DELIVERY_NOT_POSSIBLE,
        },
        {
          label: t("label.deliveryRefused"),
          value: LastMileStatusEnum.DELIVERY_REFUSED,
        },
        {
          label: t("label.deliveryInProgress"),
          value: LastMileStatusEnum.DELIVERY_IN_PROGRESS,
        },
      ],
    },
    {
      key: "type",
      name: "type",
      label: t("label.type"),
      placeholder: t("label.type"),
      value: filterState?.type,
      options: [
        { label: t("option.all"), value: "" },
        { label: t("option.boxes"), value: "BOX" },
        { label: t("option.parcels"), value: "PARCELS" },
        { label: t("option.bulk"), value: "BULK" },
        { label: t("option.loose"), value: "LOOSE" },
      ],
    },
    {
      key: "originHub",
      name: "originHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.originHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
    {
      key: "driverID",
      name: "driverID",
      label: t("label.byDriver"),
      placeholder: t("placeholder.byDriver"),
      value: filterState?.driverID,
      options: [{ label: t("option.all"), value: "" }].concat(driverList),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      originHub: selection.originHub,
      type: selection.type,
      status: selection.status,
      destination: selection.destination,
      driverID: selection.driverID,
      unassignedDriver: selection.driverID === "UNASSIGNED" && true,
    }));
  };

  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: "originHub",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            <span
              className="half-square custom-icon mb-5"
              style={{
                borderLeft: `12px solid transparent`,
                borderBottom: `12px solid #E8A51A`,
              }}
            >
              &nbsp;
            </span>
            <span>
              &nbsp;{record?.originHub?.locationName || "Cannot detect"}
            </span>
          </div>
        );
      },
    },
    {
      title: t("label.type").toLocaleUpperCase(),
      dataIndex: "type",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.id").toLocaleUpperCase(),
      dataIndex: "code",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record?.code || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            record?.type == "BOX"
              ? handleOpenBoxesTrackingView(record._id)
              : handleOpenOrderView(record._id);
          },
        };
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "deliveryStatus",
      width: 115,
      ellipsis: true,
      render: (_: any, record: any) => {
        const status =
          orderStatusesMap?.[record.deliveryStatus]?.[roles[0]["type"]];

        return (
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status ? toCapitalisedSpaced(status) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNo",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.dateTime").toLocaleUpperCase(),
      dataIndex: "updatedAt",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record?.updatedAt ? (
          <span>
            {moment(record?.updatedAt).format("DD-MM-YYYY") +
              " | " +
              moment(record?.updatedAt).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["handoverPoint", "locationName"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>{record?.handoverPoint?.locationName || "Cannot detect"}</span>
        );
      },
    },
    {
      title: t("label.driver").toLocaleUpperCase(),
      dataIndex: ["assignedDriver", "fullName"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          // supposedly response could have an assignedDriverData (check last-mile-drivers-list.tsx), but as mobile uses the same API, retain this structure till further notice
          <span>
            {typeof record?.assignedDriver === "object"
              ? record?.assignedDriver?.fullName
              : record?.assignedDriver}
          </span>
        );
      },
    },
    {
      dataIndex: ["deliveryStatus"],
      width: 80,
      render: (text: any, record: any) => {
        return (
          <>
            {showUpdateOption &&
              (text == "DELIVERY_NOT_POSSIBLE" || text == "DELIVERY_REFUSED" ? (
                <Button
                  className="dm-btn-dr"
                  type="default"
                  label={t("action.diolkoReview")}
                  size="large"
                  onClick={() => handleOpenDiolkoReviewModal(record)}
                />
              ) : (
                record?.assignedDriver !== "CUSTOMER" && (
                  <span className="">
                    <Button
                      isLoading={status === "loading"}
                      type="default"
                      label={t("action.assignDriver")}
                      size="large"
                      onClick={() => {
                        record?.type == "BOX"
                          ? handleOpenAssignDriverBoxesModal(record)
                          : handleOpenAssignDriverOthersModal(record);
                      }}
                    />
                  </span>
                )
              ))}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  useEffect(() => {
    if (!isEmpty(driverList))
      setFilterSelection && setFilterSelection(initialFilterSelection);
  }, [sortingHubOptions, driverList]);

  return (
    <ErrorBoundary>
      <div className="parcel-order-list status-selector">
        <DataTable
          rowKey={(record: any) => {
            return record?._id;
          }}
          scroll={{ x: true }}
          columns={columns}
          dataSource={parcelList || []}
          title={() => (
            <Row>
              <Col span={10} className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.searchByBoxOrTrackingNo")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
              </Col>
            </Row>
          )}
        />
      </div>

      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </ErrorBoundary>
  );
};

export default LastMileOrderList;
